/**
 * Remembers which Canvas users have imported snapshot data (task 6.0e).
 *
 * ## Why this exists rather than an API call
 *
 * `GetSnapshots` queries `PK = USER#{cognito}_CANVAS_USER#{canvasUser}`, so it
 * needs the Canvas user id up front. DynamoDB cannot prefix-match a partition
 * key, so there is no query that answers "which children have snapshots?" —
 * that needs either a table scan or a dedicated index row, which is task 6.0f.
 *
 * Task 6.0f added that index row, so `canvasUsersService.js` now asks the
 * server and this is the **fallback** it merges in when that request fails, or
 * when a just-completed import's index row did not write (the writers never
 * fail an import over it).
 *
 * **This is a convenience cache, not a source of truth.** It is per-browser and
 * does not sync across devices, so the offline setup screen must always allow a
 * user id to be entered by hand. Never gate access on this list.
 */

const STORAGE_KEY = 'offlineCanvasUsers';

const read = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((entry) => entry && entry.id) : [];
  } catch (error) {
    console.error('Could not read remembered offline users:', error);
    return [];
  }
};

const write = (entries) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    return true;
  } catch (error) {
    // Quota or a disabled store. The selector degrades to manual entry, which
    // still works, so this is a warning rather than a failure.
    console.warn('Could not remember offline user:', error);
    return false;
  }
};

/** Every remembered user, most recently imported first. */
export const getRememberedUsers = () => read();

/**
 * Record a Canvas user as having imported data.
 *
 * Idempotent: re-importing for the same child updates the timestamp and root
 * URL rather than adding a duplicate row to the selector.
 */
export const rememberUser = ({ id, localId, rootUrl, label }) => {
  if (!id) return false;

  const canvasUserId = String(id);
  const existing = read().filter((entry) => String(entry.id) !== canvasUserId);

  return write([
    {
      // MUST be the id the snapshot row is keyed on, which is the id **as
      // captured** - the local one the user typed (131973), not a global-shard
      // promotion of it. An earlier version of ImportSnapshot did promote it;
      // `_validate_canvas_user_id` records why that was wrong and removed it,
      // since no other writer does. GetSnapshots queries the partition key
      // verbatim, so an id in any other form finds no snapshots at all.
      id: canvasUserId,
      // What the user actually typed, kept only so the selector can show a
      // number they recognise. Never used for lookups.
      localId: localId ? String(localId) : null,
      rootUrl: rootUrl || null,
      label: label || null,
      lastImportedAt: new Date().toISOString()
    },
    ...existing
  ]);
};

/** Drop one remembered user. Does not delete any stored snapshot. */
export const forgetUser = (id) => {
  if (!id) return false;
  return write(read().filter((entry) => String(entry.id) !== String(id)));
};

export default { getRememberedUsers, rememberUser, forgetUser };
